import React from 'react';
import Header from './Header';
import Footer from './Footer';
import './CookieNoticePage.css';

const CookieNoticePage: React.FC = () => {
  return (
    <div className="cookie-notice-page">
      <Header />
      
      <main id="main" className="cookie-main">
        <div className="cookie-container">
          <h3 className="cookie-title">Cookie Notice</h3>
          <p className="effective-date">Effective as of September 17, 2025</p>
          
          <div className="docs-container">
            <h2 className="section-title">What does this Cookie Notice cover?</h2>
            <p>
              This Cookie Notice explains how Glistar AI Technology Limited ("Glistar AI", "we", "us" or "our") uses cookies and other similar technologies on our websites and mobile applications that link to this Cookie Notice (the "Service"), as well as in our emails. It also outlines your choices around managing these cookies and similar technologies.
            </p>
            
            <p>
              Please refer to our <a className="cookie-link" href="/privacy" rel="noopener noreferrer" target="_blank">Privacy Policy</a> for more information on our privacy practices.
            </p>

            <h2 className="section-title">Managing your preferences</h2>
            <p>
              Before we dive into the details – if you are just looking to manage your preferences on our Service relating to cookies and similar technologies (including opting-out), you can do so at any time through our Cookie Preference Centre.
            </p>
            
            <p>
              <strong>If you do not accept our cookies, you may experience some inconvenience in your use of our Service.</strong>
            </p>
            
            <p>
              If you want to find out more about cookies and similar technologies on our Service and emails, please keep reading.
            </p>

            <h2 className="section-title">What types of technologies does Glistar AI use?</h2>
            <p>
              We may use any or all of the following "technologies" on our Service and in our emails:
            </p>

            <h3 className="subsection-title">Cookies</h3>
            <p>
              A cookie is a very small text file that may be placed on your browser or in the storage on your device. There are two types: "session cookies" and "persistent cookies":
            </p>
            <ul className="content-list">
              <li><strong>Session cookies:</strong> cookies that disappear from your device or browser when you close your browser.</li>
              <li><strong>Persistent cookies:</strong> cookies that stay on your device even after you close your browser.</li>
            </ul>
            
            <p>
              Please note that both Glistar AI and third-party service providers may set cookies via our Service – these 'third party cookies' may be used to help us understand how visitors interact with our Service or to deliver content and advertising that is more relevant to you.
            </p>

            <h3 className="subsection-title">Pixel tags</h3>
            <ul className="content-list">
              <li>A pixel tag is a single pixel, transparent GIF image with a unique identifier that can recognise certain types of data on your device (similar to how cookies do). They are used to operate and improve the Service and our email practices, including to help deliver cookies, count visits and understand usage and email campaign effectiveness.</li>
              <li>For example, we may put pixel tags on emails to understand how you interact with that email (whether you opened it, forwarded it, clicked on a link, etc.). These pixels may collect the time, location and operating system of the device you use to read the email.</li>
            </ul>

            <h3 className="subsection-title">Other similar technologies</h3>
            <p>
              We may also use other tracking technologies, such as mobile advertising IDs and tags, HTML5 local storage / local shared objects (which store relevant data locally on your device), for similar purposes as described in this Cookie Notice.
            </p>
            
            <p>
              So, when we refer to cookies and "similar technologies" in this Cookie Notice, such reference includes reference to the pixel tags discussed above, HTML5 local storage / local shared objects and other equivalent tracking technologies.
            </p>

            <h2 className="section-title">Why do we use cookies and similar technologies?</h2>
            <p>
              The purposes for which we use technologies may fall into one of the following categories:
            </p>

            <ul className="content-list">
              <li><strong>Strictly necessary technologies:</strong> these technologies are essential in order to enable our Service to provide the function you have requested, such as by helping to ensure that the content of a page loads quickly and effectively.</li>
              <li><strong>Functionality technologies:</strong> these technologies allow us to provide enhanced personalisation and functionality, such as tailoring content to you, remembering your choices and preferences on the Service (e.g. language, text size, etc.) or remembering search parameters.</li>
              <li><strong>Analytics technologies:</strong> these technologies collect information on how users interact with our Service and enable us to improve how it operates and our business more widely. (For more details about how Google uses data in the context of Google Analytics, please see here: <a className="cookie-link" href="http://www.google.com/policies/privacy/partners/" rel="noopener noreferrer" target="_blank">www.google.com/policies/privacy/partners/</a>)</li>
              <li><strong>Advertising technologies:</strong> these technologies are deployed on the Service and collect information about your browsing habits in order to provide advertising which is more relevant to you and your interests – for example, they remember the websites you have visited and share that information with other parties such as advertising technology service providers.</li>
              <li><strong>Social media technologies:</strong> these technologies are used when you share information using a social media sharing or "like" button on our Service or social media platform. This information may be linked to advertising activities.</li>
            </ul>

            <h2 className="section-title">How can you manage your consent?</h2>
            <p>
              You can control or limit how cookies and similar technologies are used by taking the following steps:
            </p>
            
            <ul className="content-list">
              <li>Managing your preferences on our Service (including by opting-out) at any time by using our Cookie Preference Centre.</li>
              <li>Pixel tags in emails can typically be blocked by default by configuring your preferences in your email client to block external images or setting it to show "plain text only" emails.</li>
            </ul>
            
            <p>
              For more information about cookies, including about the cookies that have been set on your device and how to manage and delete them, visit <a className="cookie-link" href="http://www.allaboutcookies.org" rel="noopener noreferrer" target="_blank">www.allaboutcookies.org</a>. Also, to learn more about targeting and advertising cookies and your choices more widely, you can go to the <a className="cookie-link" href="https://www.networkadvertising.org/" rel="noopener noreferrer" target="_blank">Network Advertising Initiative</a>, the <a className="cookie-link" href="https://www.aboutads.info/choices/" rel="noopener noreferrer" target="_blank">Digital Advertising Alliance's Consumer Choice</a>, or if you are in Europe, <a className="cookie-link" href="https://www.youronlinechoices.com/" rel="noopener noreferrer" target="_blank">Your Online Choices</a>.
            </p>
            
            <p>
              <strong>Finally, if you do not accept certain non-essential cookies and similar technologies, you may experience some inconvenience in your use of our Service. For example, we may not be able to recognise your device and you may need to log in every time you visit our Service.</strong>
            </p>

            <h2 className="section-title">Details of Cookies and Similar Technologies</h2>
            <p>
              The specific cookies and other similar technologies that we use may include:
            </p>

            <div className="cookie-table-container">
              <table className="cookie-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Purpose</th>
                    <th>Examples</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Strictly Necessary</td>
                    <td>Enable core functionality such as security, network management, and accessibility. You may disable these by changing your browser settings, but this may affect how the Service functions.</td>
                    <td>Session management, authentication, security</td>
                  </tr>
                  <tr>
                    <td>Functional</td>
                    <td>Remember choices you make to improve your experience, such as language preferences and customized settings.</td>
                    <td>Language settings, UI preferences, remembered logins</td>
                  </tr>
                  <tr>
                    <td>Analytics</td>
                    <td>Help us understand how visitors interact with our Service by collecting and reporting information anonymously.</td>
                    <td>Google Analytics, usage statistics, performance monitoring</td>
                  </tr>
                  <tr>
                    <td>Advertising</td>
                    <td>Used to deliver relevant advertisements to you based on your interests and to measure the effectiveness of advertising campaigns.</td>
                    <td>Ad targeting, conversion tracking, remarketing</td>
                  </tr>
                  <tr>
                    <td>Social Media</td>
                    <td>Enable social media functionality and track social media interactions.</td>
                    <td>Social sharing buttons, social login, social analytics</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p>
              The details shown above (including the cookies and other similar technologies shown there) may change over time.
            </p>

            <h2 className="section-title">How we share your personal information</h2>
            <p>
              We may share information collected through cookies and similar technologies with third-party service providers who assist us in operating our Service, conducting our business, or serving our users. These third parties are obligated to keep your information confidential and secure, and to use it only for the purposes for which we disclose it to them.
            </p>
            
            <p>
              For more information about how we share your personal information, please see our <a className="cookie-link" href="/privacy" rel="noopener noreferrer" target="_blank">Privacy Policy</a>.
            </p>

            <h2 className="section-title">Changes to this Cookie Notice</h2>
            <p>
              Information about the cookies and similar technologies we use may be updated from time to time, so please check this Cookie Notice on a regular basis for any changes.
            </p>

            <h2 className="section-title" id="contactUs">Questions</h2>
            <p>
              If you have any questions about this Cookie Notice, please contact us:
            </p>
            <p><strong>Glistar AI Technology Limited</strong></p>
            <p>Email: <a className="cookie-link" href="mailto:support@glistar.ai" rel="noopener noreferrer" target="_blank">support@glistar.ai</a></p>
            <p>Website: <a className="cookie-link" href="https://glistar.ai" rel="noopener noreferrer" target="_blank">https://glistar.ai</a></p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CookieNoticePage;

